import React, { useState } from 'react';
import { TrophyIcon, StarIcon, LockIcon, ChevronRightIcon, SearchIcon, FilterIcon, UserIcon, BookIcon, TargetIcon, HeartIcon, MessageSquareIcon, BrainIcon, XIcon } from 'lucide-react';
interface Trophy {
  id: number;
  emoji: string;
  title: string;
  description: string;
  category: 'Engagement' | 'Goals' | 'Learning' | 'Community' | 'Leadership';
  points: number;
  progress?: number;
  total?: number;
  date?: string;
  earned: boolean;
  rarity: 'Common' | 'Rare' | 'Epic' | 'Legendary';
}
const Trophies: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showTrophyDetails, setShowTrophyDetails] = useState<Trophy | null>(null);
  const categories = [{
    name: 'Engagement',
    icon: MessageSquareIcon,
    color: 'text-blue-500'
  }, {
    name: 'Goals',
    icon: TargetIcon,
    color: 'text-green-500'
  }, {
    name: 'Learning',
    icon: BookIcon,
    color: 'text-yellow-500'
  }, {
    name: 'Community',
    icon: HeartIcon,
    color: 'text-red-500'
  }, {
    name: 'Leadership',
    icon: UserIcon,
    color: 'text-purple-500'
  }];
  const trophies: Trophy[] = [{
    id: 1,
    emoji: '🌟',
    title: 'First Goal Set',
    description: "You've set your first mentorship goal!",
    category: 'Goals',
    points: 50,
    earned: true,
    date: 'Feb 1, 2024',
    rarity: 'Common'
  }, {
    id: 2,
    emoji: '🎯',
    title: 'Goal Achiever',
    description: 'Complete your first goal',
    category: 'Goals',
    points: 100,
    earned: true,
    date: 'Feb 10, 2024',
    rarity: 'Common'
  }, {
    id: 3,
    emoji: '📅',
    title: 'Meeting Master',
    description: 'Attend 5 mentorship sessions',
    category: 'Engagement',
    points: 75,
    progress: 5,
    total: 5,
    earned: true,
    date: 'Feb 15, 2024',
    rarity: 'Common'
  }, {
    id: 4,
    emoji: '💫',
    title: 'Rising Star',
    description: 'Complete 5 goals',
    category: 'Goals',
    points: 200,
    progress: 2,
    total: 5,
    earned: false,
    rarity: 'Rare'
  }, {
    id: 5,
    emoji: '🧠',
    title: 'Knowledge Seeker',
    description: 'Access 10 learning resources',
    category: 'Learning',
    points: 150,
    progress: 4,
    total: 10,
    earned: false,
    rarity: 'Common'
  }, {
    id: 6,
    emoji: '🤝',
    title: 'Community Builder',
    description: 'Engage with 3 community discussions',
    category: 'Community',
    points: 100,
    progress: 1,
    total: 3,
    earned: false,
    rarity: 'Rare'
  }, {
    id: 7,
    emoji: '👑',
    title: 'Mentorship Elite',
    description: 'Complete a 6-month mentorship journey',
    category: 'Leadership',
    points: 500,
    earned: false,
    rarity: 'Legendary'
  }];
  const filteredTrophies = trophies.filter(trophy => {
    const matchesSearch = trophy.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory ? trophy.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });
  const totalPoints = trophies.filter(t => t.earned).reduce((sum, trophy) => sum + trophy.points, 0);
  const TrophyDetailsModal = ({
    trophy
  }: {
    trophy: Trophy;
  }) => <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-1996">
      <div className="bg-white rounded-xl w-full max-w-md p-6" data-id="element-1997">
        <div className="flex justify-between items-start mb-6" data-id="element-1998">
          <div className="flex items-center space-x-4" data-id="element-1999">
            <div className="text-4xl" data-id="element-2000">{trophy.emoji}</div>
            <div data-id="element-2001">
              <h3 className="font-semibold text-lg" data-id="element-2002">{trophy.title}</h3>
              <div className="flex items-center space-x-2" data-id="element-2003">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getRarityStyle(trophy.rarity)}`} data-id="element-2004">
                  {trophy.rarity}
                </span>
                <span className="text-sm text-purple-600" data-id="element-2005">
                  +{trophy.points} pts
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => setShowTrophyDetails(null)} data-id="element-2006">
            <XIcon size={20} className="text-gray-400" data-id="element-2007" />
          </button>
        </div>
        <p className="text-gray-600 mb-4" data-id="element-2008">{trophy.description}</p>
        {trophy.progress !== undefined && trophy.total !== undefined && <div className="mb-4" data-id="element-2009">
            <div className="flex justify-between text-sm mb-1" data-id="element-2010">
              <span className="text-gray-600" data-id="element-2011">Progress</span>
              <span className="font-medium" data-id="element-2012">
                {trophy.progress} / {trophy.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2013">
              <div className="bg-purple-600 rounded-full h-2 transition-all duration-300" style={{
            width: `${trophy.progress / trophy.total * 100}%`
          }} data-id="element-2014" />
            </div>
          </div>}
        {trophy.date && <p className="text-sm text-gray-500" data-id="element-2015">Earned on {trophy.date}</p>}
        <div className="mt-6 flex justify-end" data-id="element-2016">
          <button onClick={() => setShowTrophyDetails(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" data-id="element-2017">
            Close
          </button>
        </div>
      </div>
    </div>;
  const getRarityStyle = (rarity: Trophy['rarity']) => {
    switch (rarity) {
      case 'Common':
        return 'bg-gray-100 text-gray-600';
      case 'Rare':
        return 'bg-blue-100 text-blue-600';
      case 'Epic':
        return 'bg-purple-100 text-purple-600';
      case 'Legendary':
        return 'bg-yellow-100 text-yellow-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  return <div className="pb-4" data-id="element-2018">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 -mx-4 px-4 pt-8 pb-6 mb-6" data-id="element-2019">
        <div className="flex items-center justify-between mb-4" data-id="element-2020">
          <h1 className="text-2xl font-bold text-white" data-id="element-2021">Your Trophies</h1>
          <div className="flex items-center bg-white bg-opacity-20 rounded-lg px-3 py-1" data-id="element-2022">
            <TrophyIcon className="text-yellow-300 mr-2" size={20} data-id="element-2023" />
            <span className="text-white font-medium" data-id="element-2024">{totalPoints} Points</span>
          </div>
        </div>
        <div className="flex items-center space-x-4 overflow-x-auto pb-2" data-id="element-2025">
          <button onClick={() => setSelectedCategory(null)} className={`flex items-center px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${!selectedCategory ? 'bg-white text-purple-600' : 'text-white bg-white bg-opacity-20'}`} data-id="element-2026">
            All Trophies
          </button>
          {categories.map(category => <button key={category.name} onClick={() => setSelectedCategory(category.name)} className={`flex items-center px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === category.name ? 'bg-white text-purple-600' : 'text-white bg-white bg-opacity-20'}`} data-id="element-2027">
              <category.icon size={16} className="mr-1.5" data-id="element-2028" />
              {category.name}
            </button>)}
        </div>
      </div>
      <div className="mb-6" data-id="element-2029">
        <div className="relative" data-id="element-2030">
          <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-2031" />
          <input type="text" placeholder="Search trophies..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2032" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2033">
        {filteredTrophies.map(trophy => <div key={trophy.id} onClick={() => setShowTrophyDetails(trophy)} className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${trophy.earned ? 'border-purple-200 hover:border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`} data-id="element-2034">
            <div className="flex justify-between items-start mb-3" data-id="element-2035">
              <div className="text-3xl" data-id="element-2036">{trophy.emoji}</div>
              <div className={`text-xs px-2 py-0.5 rounded-full ${getRarityStyle(trophy.rarity)}`} data-id="element-2037">
                {trophy.rarity}
              </div>
            </div>
            <h3 className="font-medium mb-1" data-id="element-2038">{trophy.title}</h3>
            <p className="text-sm text-gray-600 mb-3" data-id="element-2039">{trophy.description}</p>
            {trophy.progress !== undefined && trophy.total !== undefined && <div className="mb-3" data-id="element-2040">
                <div className="flex justify-between text-sm mb-1" data-id="element-2041">
                  <span className="text-gray-600" data-id="element-2042">Progress</span>
                  <span className="font-medium" data-id="element-2043">
                    {trophy.progress} / {trophy.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-2044">
                  <div className="bg-purple-600 rounded-full h-1.5 transition-all duration-300" style={{
              width: `${trophy.progress / trophy.total * 100}%`
            }} data-id="element-2045" />
                </div>
              </div>}
            <div className="flex items-center justify-between" data-id="element-2046">
              <div className="flex items-center" data-id="element-2047">
                {trophy.earned ? <StarIcon size={16} className="text-yellow-500 fill-yellow-500 mr-1" data-id="element-2048" /> : <LockIcon size={16} className="text-gray-400 mr-1" data-id="element-2049" />}
                <span className={`text-sm ${trophy.earned ? 'text-yellow-500' : 'text-gray-500'}`} data-id="element-2050">
                  {trophy.points} pts
                </span>
              </div>
              <ChevronRightIcon size={16} className="text-gray-400" data-id="element-2051" />
            </div>
          </div>)}
      </div>
      {showTrophyDetails && <TrophyDetailsModal trophy={showTrophyDetails} data-id="element-2052" />}
    </div>;
};
export default Trophies;